import { useEffect, useState } from 'react';
import { Bell, BellOff, BellRing } from 'lucide-react';
import { Button, Card, EmptyState, NativeSelect, toast } from '@vwt/ui';
import type { NotificationPreference } from '@vwt/contracts';
import { useApiMutation, useApiQuery } from '../../lib/api.js';
import { toastError } from '../../lib/errors.js';
import { useTranslation } from '../../lib/i18n.js';
import { useSession } from '../../lib/session.js';
import { disablePush, enablePush, pushState, PushPermissionError, type PushState } from '../../lib/push.js';
import { QueryState, SectionTitle, TimeAgo } from '../../components/common.js';

const EVENTS = [
  'REQUEST_RECEIVED',
  'REQUEST_NEEDS_INFORMATION',
  'REQUEST_ACCEPTED',
  'REQUEST_REJECTED',
  'REQUEST_WORK_STARTED',
  'REQUEST_RESOLVED',
  'REQUEST_NEEDS_ATTENTION',
  'WORK_ASSIGNED',
  'WORK_BLOCKED',
  'COMMENT_ADDED',
  'MENTIONED',
  'WORK_DUE_SOON',
  'WORK_OVERDUE',
  'WORK_STALE',
  'WORK_AUTOMATION',
] as const;
type Event = (typeof EVENTS)[number];

/** Server defaults (Section 10.2): requester events, assignments and mentions email immediately. */
const EMAIL_DEFAULT_ON = new Set<Event>(['REQUEST_RECEIVED', 'REQUEST_NEEDS_INFORMATION', 'REQUEST_ACCEPTED', 'REQUEST_REJECTED', 'REQUEST_WORK_STARTED', 'REQUEST_RESOLVED', 'REQUEST_NEEDS_ATTENTION', 'WORK_ASSIGNED', 'MENTIONED', 'WORK_DUE_SOON', 'WORK_OVERDUE']);

/** In-app inbox, browser notifications for this device, and per-event email and browser settings. */
export function NotificationPreferences() {
  const { t } = useTranslation();
  const { runtime } = useSession();
  const browserEnabled = Boolean(runtime?.webPush.enabled);
  const inbox = useApiQuery('listNotifications', { query: { limit: '30' } }, { retry: false });
  const prefs = useApiQuery('getNotificationPreferences', {}, { retry: false });
  const save = useApiMutation('setNotificationPreferences', { invalidate: ['getNotificationPreferences'] });
  const markRead = useApiMutation('markNotificationsRead', { invalidate: ['listNotifications'] });
  const current = new Map((prefs.data?.data ?? []).map((p) => [`${p.channel}:${p.event}`, p.frequency]));
  const set = async (channel: 'EMAIL' | 'BROWSER', event: Event, frequency: string) => {
    const next: NotificationPreference[] = [...(prefs.data?.data ?? []).filter((p) => !(p.channel === channel && p.event === event)), { channel, event, frequency: frequency as 'IMMEDIATE' }];
    try {
      await save.mutateAsync({ body: { preferences: next } });
      toast.success(t('common.changesSaved'));
    } catch (error) {
      toastError(error);
    }
  };
  return (
    <div className="flex flex-col gap-6">
      {browserEnabled && runtime?.webPush.publicKey ? <BrowserNotifications publicKey={runtime.webPush.publicKey} /> : null}
      <Card>
        <QueryState query={inbox} empty={inbox.data?.data.length === 0 ? <EmptyState icon={<Bell />} title={t('common.noResults')} /> : undefined}>
          <ul className="divide-y divide-border">
            {(inbox.data?.data ?? []).map((n) => (
              <li key={n.id} className={`flex items-start gap-3 px-4 py-3 ${n.readAt ? '' : 'bg-accent-soft/20'}`}>
                <Bell className="mt-0.5 size-4 text-fg-subtle" />
                <div className="min-w-0 flex-1">
                  <a href={n.link ?? '#'} className="text-sm font-medium hover:underline" onClick={() => void markRead.mutateAsync({ body: { ids: [n.id] } })}>
                    {n.title}
                  </a>
                  <p className="text-sm text-fg-muted">{n.body}</p>
                </div>
                <span className="text-xs">
                  <TimeAgo iso={n.createdAt} />
                </span>
              </li>
            ))}
          </ul>
        </QueryState>
      </Card>
      <Card className="p-5">
        <SectionTitle>{t('notifications.preferences.title')}</SectionTitle>
        <QueryState query={prefs}>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-fg-subtle">
                  <th className="py-2 font-medium">{t('notifications.preferences.event')}</th>
                  <th className="py-2 font-medium">{t('notifications.preferences.email')}</th>
                  {browserEnabled ? <th className="py-2 font-medium">{t('notifications.preferences.browser')}</th> : null}
                </tr>
              </thead>
              <tbody>
                {EVENTS.map((event) => (
                  <tr key={event} className="border-t border-border">
                    <td className="py-2 pr-3">{t(`notifications.events.${event}`)}</td>
                    <td className="py-2 pr-3">
                      <NativeSelect
                        className="h-7 w-40"
                        value={current.get(`EMAIL:${event}`) ?? (EMAIL_DEFAULT_ON.has(event) ? 'IMMEDIATE' : 'MUTED')}
                        onChange={(e) => void set('EMAIL', event, e.target.value)}
                        aria-label={`${t('notifications.preferences.email')}: ${t(`notifications.events.${event}`)}`}
                      >
                        <option value="IMMEDIATE">{t('notifications.preferences.immediate')}</option>
                        <option value="DAILY_DIGEST">{t('notifications.preferences.digest')}</option>
                        <option value="MUTED">{t('notifications.preferences.muted')}</option>
                      </NativeSelect>
                    </td>
                    {browserEnabled ? (
                      <td className="py-2">
                        <NativeSelect
                          className="h-7 w-32"
                          value={current.get(`BROWSER:${event}`) === 'MUTED' ? 'MUTED' : 'IMMEDIATE'}
                          onChange={(e) => void set('BROWSER', event, e.target.value)}
                          aria-label={`${t('notifications.preferences.browser')}: ${t(`notifications.events.${event}`)}`}
                        >
                          <option value="IMMEDIATE">{t('notifications.preferences.on')}</option>
                          <option value="MUTED">{t('notifications.preferences.off')}</option>
                        </NativeSelect>
                      </td>
                    ) : null}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </QueryState>
      </Card>
    </div>
  );
}

/** Turn browser notifications on or off for this device, test them, and forget other devices. */
function BrowserNotifications({ publicKey }: { publicKey: string }) {
  const { t } = useTranslation();
  const [state, setState] = useState<PushState | null>(null);
  const [busy, setBusy] = useState(false);
  const devices = useApiQuery('listPushSubscriptions', {}, { retry: false });
  const test = useApiMutation('sendTestPush');
  const remove = useApiMutation('deletePushSubscription', { invalidate: ['listPushSubscriptions'] });

  useEffect(() => {
    void pushState().then(setState);
  }, []);

  const turnOn = async () => {
    setBusy(true);
    try {
      await enablePush(publicKey);
      setState('on');
      await devices.refetch();
      toast.success(t('notifications.browser.enabled'));
    } catch (error) {
      if (error instanceof PushPermissionError) {
        setState(error.permission === 'denied' ? 'blocked' : 'off');
        if (error.permission === 'denied') toast.error(t('notifications.browser.blockedHelp'));
      } else toastError(error);
    } finally {
      setBusy(false);
    }
  };
  const turnOff = async () => {
    setBusy(true);
    try {
      await disablePush();
      setState('off');
      await devices.refetch();
      toast.success(t('notifications.browser.disabled'));
    } catch (error) {
      toastError(error);
    } finally {
      setBusy(false);
    }
  };
  const sendTest = async () => {
    try {
      const result = await test.mutateAsync({});
      if (result?.data.sent) toast.success(t('notifications.browser.testSent'));
      else toast.error(t('notifications.browser.testNone'));
    } catch (error) {
      toastError(error);
    }
  };

  return (
    <Card className="p-5">
      <SectionTitle>{t('notifications.browser.title')}</SectionTitle>
      <div className="flex flex-wrap items-center gap-3">
        {state === 'on' ? <BellRing className="size-5 text-accent" /> : <BellOff className="size-5 text-fg-subtle" />}
        <p className="min-w-0 flex-1 text-sm text-fg-muted" role="status">
          {state === null ? '' : t(`notifications.browser.state.${state}`)}
        </p>
        {state === 'off' ? (
          <Button variant="primary" loading={busy} onClick={() => void turnOn()}>
            {t('notifications.browser.enable')}
          </Button>
        ) : null}
        {state === 'on' ? (
          <>
            <Button variant="secondary" loading={test.isPending} onClick={() => void sendTest()}>
              {t('notifications.browser.test')}
            </Button>
            <Button variant="ghost" loading={busy} onClick={() => void turnOff()}>
              {t('notifications.browser.disable')}
            </Button>
          </>
        ) : null}
      </div>
      {devices.data?.data.length ? (
        <ul className="mt-4 flex flex-col divide-y divide-border text-sm" aria-label={t('notifications.browser.devices')}>
          {devices.data.data.map((d) => (
            <li key={d.id} className="flex items-center gap-2 py-2">
              <span className="min-w-0 flex-1 truncate">{d.label ?? t('notifications.browser.unknownDevice')}</span>
              <span className="text-xs text-fg-subtle">
                <TimeAgo iso={d.lastSuccessAt ?? d.createdAt} />
              </span>
              <Button size="sm" variant="ghost" loading={remove.isPending && remove.variables?.params.id === d.id} onClick={() => void remove.mutateAsync({ params: { id: d.id } }).catch(toastError)}>
                {t('notifications.browser.forget')}
              </Button>
            </li>
          ))}
        </ul>
      ) : null}
    </Card>
  );
}
